/**
 * utils/syncInitPolicy.ts
 * Runtime policy functions for sync initialization decisions
 *
 * Separated from type definitions per CODING_STANDARDS (src/types/ is type-only).
 *
 * @see SyncInitTypes.ts for type definitions (SyncInitReason, ReconnectModalType, etc.)
 */

import type { SyncInitReason, ReconnectModalType } from '@/types/sync/SyncInitTypes';
import type { ProviderKey } from '@/types/sync/ProviderTypes';

/**
 * Map sync initialization outcome to required modal type
 *
 * Centralizes the reason→modal contract to prevent switch/case duplication
 * in handlers (SyncInitializationService, AppWrapper, etc.)
 *
 * Modal Policy Rationale:
 * - MODAL (blocking): Errors requiring explicit user action to resolve
 *   → TOKEN_EXPIRED, TOKEN_REVOKED, PERMISSION_DENIED, DIRECTORY_INVALID
 *   (User must re-authenticate, re-grant permission, or fix directory path)
 * - NON-MODAL (banner/toast): Transient errors or user-initiated cancellations
 *   → USER_CANCELLED, ACCESS_DENIED, INSUFFICIENT_SCOPES, OFFLINE, RATE_LIMITED, TIMEOUT, PROVIDER_OUTAGE, ABORTED, UNKNOWN_ERROR
 *   (Auto-retry possible, or user explicitly cancelled/denied)
 * - SUCCESS: No modal needed
 *
 * Future providers should follow this rule: if error requires explicit user intervention, show modal.
 *
 * @param reason - Sync initialization outcome reason
 * @param provider - Optional provider type for fallback modal detection
 * @returns Modal type to show user, or null if no modal needed
 */
export function getRequiredModal(
  reason: SyncInitReason,
  provider?: ProviderKey
): ReconnectModalType | null {
  switch (reason) {
    // Success - no modal needed
    case 'SUCCESS':
      return null;

    // Modal errors - OAuth token issues (Google Drive only)
    case 'TOKEN_EXPIRED':
    case 'TOKEN_REVOKED':
      return 'RECONNECT_GOOGLE_DRIVE';

    // Modal errors - permission/directory issues (provider-specific)
    case 'PERMISSION_DENIED':
    case 'DIRECTORY_INVALID':
      // PERMISSION_DENIED can occur for both providers, use fallback
      if (reason === 'PERMISSION_DENIED' && provider === 'google-drive') {
        return 'RECONNECT_GOOGLE_DRIVE';
      }
      return 'RECONNECT_LOCAL_DIRECTORY';

    // Non-modal errors - handle via banner/toast (transient or user-initiated)
    case 'USER_CANCELLED':
    case 'ACCESS_DENIED':
    case 'INSUFFICIENT_SCOPES':
    case 'OFFLINE':
    case 'RATE_LIMITED':
    case 'TIMEOUT':
    case 'PROVIDER_OUTAGE':
    case 'ABORTED':
    case 'UNKNOWN_ERROR':
      return null;

    default:
      // TODO: Future-proof for new providers - add RECONNECT_GENERIC modal type
      // to avoid hardcoding provider-specific modals in default branch
      if (provider === 'google-drive') return 'RECONNECT_GOOGLE_DRIVE';
      if (provider === 'local') return 'RECONNECT_LOCAL_DIRECTORY';
      return null;
  }
}

/**
 * Check if a failure reason is retry-able (for Task 2.F retry logic)
 *
 * Retry Policy:
 * - RETRYABLE (auto-retry with backoff): Transient network/provider issues
 *   → OFFLINE, PROVIDER_OUTAGE, RATE_LIMITED, TIMEOUT
 * - NON-RETRYABLE (require user action): Auth/permission/configuration errors
 *   → TOKEN_EXPIRED, TOKEN_REVOKED, PERMISSION_DENIED, DIRECTORY_INVALID, USER_CANCELLED, ACCESS_DENIED, INSUFFICIENT_SCOPES, ABORTED, UNKNOWN_ERROR
 * - SUCCESS: Not applicable (not a failure)
 *
 * Special cases:
 * - TOKEN_EXPIRED: Handled via modal (user initiates retry after re-auth)
 * - RATE_LIMITED: Retryable, but use retryAfterMs from response if available
 *
 * @param reason - Sync initialization outcome reason
 * @returns True if should retry automatically with backoff, false otherwise
 */
export function shouldRetryReason(reason: SyncInitReason): boolean {
  // Explicitly retryable: transient network/provider issues
  return (
    reason === 'OFFLINE' ||           // Network connection lost
    reason === 'PROVIDER_OUTAGE' ||   // Provider API unavailable
    reason === 'RATE_LIMITED' ||      // Provider rate limit (429) - use retryAfterMs
    reason === 'TIMEOUT'              // Connection timeout (network or provider)
  );
  // Explicitly non-retryable (all other reasons):
  // - 'SUCCESS': Not a failure
  // - 'TOKEN_EXPIRED': Modal flow (user re-authenticates)
  // - 'TOKEN_REVOKED': Modal flow (user re-authorizes)
  // - 'PERMISSION_DENIED': Modal flow (user re-grants permission)
  // - 'DIRECTORY_INVALID': Modal flow (user selects new directory)
  // - 'USER_CANCELLED': User explicitly cancelled, do not retry
  // - 'ACCESS_DENIED': User denied OAuth consent, do not retry
  // - 'INSUFFICIENT_SCOPES': User denied required scopes, do not retry
  // - 'ABORTED': Programmatic cancellation (AbortSignal), do not retry
  // - 'UNKNOWN_ERROR': Unknown failure, do not retry (may cause infinite loop)
}
